// NOTE: When you add or change markdown syntax here, also update the Markdown Preview page in the
// management app (apps/management-app/src/screens/MarkdownPreview/syntax-reference.ts). See ./README.md.
import type { Paragraph, PhrasingContent, Root, RootContent, Strong } from 'mdast';
import type { Pluggable } from 'unified';
import { visit } from 'unist-util-visit';
import type { SectionTag } from '@trinity/chat-components/components/presentation/SectionChip';

// Section tags: `**[Validated]**`, `**[Directional]**`, `**[Low confidence]**`. They can appear
// anywhere in a response.
// - A tag opening a paragraph starts a section that runs until the next tag.
// - A tag elsewhere (e.g. trailing the text it labels) renders its chip where it was written.
const SECTION_ORDER: Record<SectionTag, number> = {
  Validated: 0,
  Directional: 1,
  'Low confidence': 2,
};

const SECTION_TAG_RE = /^\[(Validated|Directional|Low confidence)\]$/;

type Section = { tag: SectionTag; nodes: RootContent[] };

function textOf(nodes: PhrasingContent[]): string {
  return nodes
    .map((node) => {
      if ('value' in node) return node.value;
      return 'children' in node ? textOf(node.children) : '';
    })
    .join('');
}

function getStrongTag(node: PhrasingContent | undefined): SectionTag | null {
  if (node?.type !== 'strong') return null;
  const match = SECTION_TAG_RE.exec(textOf(node.children));
  return match ? (match[1] as SectionTag) : null;
}

// A paragraph that opens with a tag starts a section (the tag leads its content).
function getSectionTag(node: RootContent): SectionTag | null {
  return node.type === 'paragraph' ? getStrongTag(node.children[0]) : null;
}

// `inline` tags sit inside a paragraph (lists, quotes), so they render without the block wrapper.
function createSectionTagNode(tag: SectionTag, inline = false): RootContent {
  return { type: 'sectionTag', data: { hName: 'sectionTag', hProperties: { tag, inline } } } as unknown as RootContent;
}

// Drops the tag from its paragraph, keeping whatever followed it (the rest of the paragraph is content).
function stripTagFromParagraph(node: Paragraph): RootContent[] {
  const rest = node.children.slice(1);
  const [next] = rest;
  if (next?.type === 'text') {
    const value = next.value.trimStart();
    if (value) rest[0] = { ...next, value };
    else rest.shift();
  }
  return rest.length ? [{ ...node, children: rest }] : [];
}

function trimPhrasing(children: PhrasingContent[]): PhrasingContent[] {
  const trimmed = [...children];
  const first = trimmed[0];
  if (first?.type === 'text') trimmed[0] = { ...first, value: first.value.trimStart() };
  const last = trimmed[trimmed.length - 1];
  if (last?.type === 'text') trimmed[trimmed.length - 1] = { ...last, value: last.value.trimEnd() };
  return trimmed.filter((child) => child.type !== 'text' || child.value.length > 0);
}

// A tag elsewhere in a paragraph (typically trailing the text it labels) is split out into its own
// block, keeping its position: text before, the chip, text after.
function splitParagraphAtTags(node: Paragraph): RootContent[] {
  const result: RootContent[] = [];
  let buffer: PhrasingContent[] = [];

  const flush = (): void => {
    const children = trimPhrasing(buffer);
    if (children.length > 0) result.push({ ...node, children });
    buffer = [];
  };

  for (const child of node.children) {
    const tag = getStrongTag(child);
    if (tag) {
      flush();
      result.push(createSectionTagNode(tag));
    } else {
      buffer.push(child);
    }
  }
  flush();
  return result;
}

function buildSection(tag: SectionTag, content: RootContent[]): Section {
  return { tag, nodes: [createSectionTagNode(tag), ...content] };
}

// Groups top-level content under the tags that lead it, in place.
function groupLeadingSections(tree: Root): void {
  const preamble: RootContent[] = [];
  const sections: { tag: SectionTag; content: RootContent[] }[] = [];

  for (const node of tree.children) {
    const tag = getSectionTag(node);
    if (tag) {
      sections.push({ tag, content: node.type === 'paragraph' ? stripTagFromParagraph(node) : [] });
    } else if (sections.length > 0) {
      sections[sections.length - 1].content.push(node);
    } else {
      preamble.push(node);
    }
  }

  if (sections.length === 0) return;

  const built = sections.map((section) => buildSection(section.tag, section.content));

  // Only reorder when sections follow the standard assessment pattern (all same-tag sections
  // grouped). Engagement scripts interleave tags intentionally (Directional → Validated →
  // Directional) — preserve that order. Skip reordering when a preamble exists (survey Q labels
  // depend on order).
  if (preamble.length === 0) {
    const isAlreadyGrouped = built.every(
      (s, i) => i === 0 || SECTION_ORDER[s.tag] >= SECTION_ORDER[built[i - 1].tag]
    );
    if (isAlreadyGrouped) {
      built.sort((a, b) => SECTION_ORDER[a.tag] - SECTION_ORDER[b.tag]);
    }
  }

  tree.children = [...preamble, ...built.flatMap((section) => section.nodes)];
}

export const remarkSectionTags: Pluggable = () => {
  return (tree) => {
    const root = tree as Root;
    groupLeadingSections(root);

    root.children = root.children.flatMap((node) =>
      node.type === 'paragraph' && node.children.some((child) => getStrongTag(child))
        ? splitParagraphAtTags(node)
        : [node]
    );

    // Tags nested in lists, quotes or tables stay inline.
    visit(tree, 'strong', (node: Strong, index, parent) => {
      const tag = getStrongTag(node);
      if (!tag || !parent || typeof index !== 'number') return;
      parent.children.splice(index, 1, createSectionTagNode(tag, true));
      return index + 1;
    });
  };
};
